import * as React from "react";
import { useCallback } from "react";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import FormDialog from "../../../../components/Dialog/FormDialog";
import { useResponsive } from "../../../../hooks/useResponsive";
import ReactQuill from "react-quill";
import { ReactQuillsProps, removeHtmlTagsAndEntities } from "../../../../utils";
import { useBoardDocumentState } from "./hooks/useBoardDocumentState";

export default React.memo(function BoardDocument({
    boardName,
    setShowSummary,
    showSummary,
    boardId,
    isPermission,
}) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
    const isMobile = useResponsive("down", "md");
    const { summary, setSummary, isLoadingDocument, isPending, handleClose } =
        useBoardDocumentState({
            boardId,
            showSummary,
            setShowSummary,
            isPermission,
        });

    const getQuillModules = useCallback(() => {
        if (!isPermission) {
            return {
                ...ReactQuillsProps.modules,
                toolbar: false, // Disable toolbar for read-only mode
            };
        }
        return ReactQuillsProps.modules;
    }, [isPermission]);

    return (
        <FormDialog
            open={showSummary}
            fullScreen={fullScreen}
            title={boardName}
            handleClose={handleClose}
            fullWidth={false}
            sx={{
                zIndex: 1200,
                "& .MuiDialog-container": {
                    "& .MuiPaper-root": {
                        width: "100%",
                        height: isMobile ? "100vh" : "calc(100vh - 30px)",
                        borderRadius: isMobile ? "" : "12px",
                        margin: isMobile ? "auto" : "15px auto",
                        maxWidth: "1180px",
                    },
                },
            }}
        >
            <Typography>
                <p className="summary-discription-title">A shared space for important info — links, notes, and images, all saved automatically.</p>
                <div className="text-editor" style={{ position: "relative" }}>
                    {/* Loading or Saving Overlay */}
                    {(isLoadingDocument || isPending) && (
                        <div
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "rgba(255, 255, 255, 0.6)",
                                zIndex: 2,
                                fontSize: "16px",
                                color: "#444",
                                pointerEvents: "none",
                            }}
                        >
                            {isLoadingDocument ? "Loading..." : "Saving..."}
                        </div>
                    )}
                    <ReactQuill
                        value={summary}
                        modules={getQuillModules()}
                        formats={ReactQuillsProps.formats}
                        className="react-quill-description"
                        onChange={useCallback((value) => {
                            if (!isPermission) return;
                            const cleanValue =
                                removeHtmlTagsAndEntities(value).length === 0
                                    ? ""
                                    : value;
                            setSummary(cleanValue);
                        }, [isPermission, setSummary])}
                        placeholder={isPermission ? "Type Here..." : ""}
                        readOnly={!isPermission}
                        style={{
                            height: isMobile ? "170px" : "calc(96vh - 170px)",
                            zIndex: 1,
                            cursor: isPermission ? "text" : "default",
                        }}
                    />
               </div>
            </Typography>
        </FormDialog>
    );

});
